export enum BSAEnv {
  DV = 'dv',
  QA = 'qa',
  PP = 'pp',
  PD = 'pd',
}

export enum RankStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum UnparsedBoolean {
  False = 'False',
  True = 'True',
}

/**
 * Used in some den endpoints where the person is inside a Den subunit
 */
export enum DenType {
  LION = 'lions',
  BOBCAT = 'bobcat',
  TIGER = 'tigers',
  WOLF = 'wolves',
  BEAR = 'bears',
  WEBELOS = 'webelos',
  AOL = 'aol',
}

/**
 * Payment logs lookup types
 */
export enum PaymentTypes {
  ONLINE = 'Online',
  CREDIT = 'Credit',
  PAYMENT = 'Payment',
  CHARGE = 'Charge',
  DEBIT = 'Debit',
}

export enum SpecialPaymentTypes {
  DEPOSIT = 'ScoutAccountDeposit',
}

/**
 * Payment logs status types
 */
export enum PaymentStatus {
  DUE = 'due',
  PAST_DUE = 'past-due',
}

/**
 * Online payment status from Stripe. Populated by lambda
 */
export enum StripePaymentStatus {
  /** Occurs when a PaymentIntent has started processing */
  PROCESSING = 'processing',
  /**	Occurs whenever a pending charge is created */
  PENDING = 'pending',
  /**	Occurs whenever a charge is successful. */
  RELEASED = 'succeeded',
  /**	Occurs whenever a failed charge attempt occurs. */
  FAILED = 'failed',
  /**	Occurs whenever a refund from a customer cash balance is created */
  REFUNDED = 'refunded',
  /**	Occurs whenever an uncaptured charge expires	chargedback	Occurs whenever a customer disputes a charge with their bank */
  EXPIRED = 'expired',
  /**	Occurs when a PaymentIntent is canceled. */
  CANCELLED = 'canceled',
}

/**
 * Payment logs sources for create request
 */
export enum PaymentSources {
  USER = 'User',
  UNIT = 'Unit',
}

/**
 * IDs of Relationship types got from /lookups/person/relationshipTypes
 */
export enum PersonRelationShipType {
  FatherOf = 1,
  MotherOf = 2,
  SonOf = 3,
}

export enum UnitStatus {
  Active = 'active',
  Lapsed = 'lapsed',
  Expired = 'expired',
  All = 'all',
  Unposted = 'Unposted',
}

/** This come as-is in the API, do not customize */
export enum AdvancementStatus {
  Awarded = 'Awarded',
  LeaderApproved = 'Leader Approved',
  /** For Merit Badges */
  LeaderSigned = 'Leader Signed',
  CounselorApproved = 'Counselor Approved',
  Completed = 'Completed',
  Started = 'Started',
  NotStarted = 'Not Started',
}

/** Used on pending items status */
export enum PendingItemStatus {
  STARTED = 'Started',
  LEADER_SIGNED = 'LeaderSigned',
  COUNSELOR_APPROVED = 'CounselorApproved',
  LEADER_APPROVED = 'LeaderApproved',
  COMPLETED = 'Completed',
  AWARDED = 'Awarded',
}

/** Used on merit badges blue card */
export enum BlueCardAdvancementStatus {
  STARTED = 'Started',
  LEADER_SIGNED = 'Leader Signed',
  COMPLETED = 'Completed',
  COUNSELOR_APPROVED = 'Counselor Approved',
  LEADER_APPROVED = 'Leader Approved',
  AWARDED = 'Awarded',
}

/** Used only if merit badge is not started before blue card */
export enum BlueCardAdvancementNotStarted {
  NOT_STARTED = 'Not Started',
}

export enum PendingItemType {
  MERIT_BADGES = 'meritBadges',
  RANKS = 'ranks',
  AWARDS = 'awards',
  ADVENTURES = 'adventures',
  ACTIVITIES = 'activities',
}

export enum Gender {
  F = 'F',
  M = 'M',
}

/**
 * (2024-10-16) Computed by ESB
 */
export enum RenewalStatus {
  Expired = 'Expired',
  OptedOut = 'Opted Out',
  Renewed = 'Renewed',
  Eligible = 'Eligible to Renew',
  Current = 'Current',
  /*Custom value for this app only, means that the user doesn't have an entry in the /org<Adult|Youth|Parent> API*/
  NeedsToRegister = 'Needs to Register',
}

export enum RegistrationSituationStatus {
  Expired = 'expiredRegistration',
  Lapsed = 'lapsedRegistration',
  Active = 'activeRegistration',
}

export enum ProfileAttributes {
  Phone = 'phone',
  Address = 'address',
  Email = 'email',
  WebContact = 'webContact',
  Handicap = 'handicap',
  SpecialNeeds = 'specialNeeds',
  Religion = 'religion',
  Hobbies = 'hobbies',
  Education = 'education',
  MyBio = 'myBio',
  Employer = 'employer',
  Grade = 'grade',
  School = 'school',
}

export enum RenewalOrderStatus {
  Initiated = 'Initiated', // 1,
  Submitted = 'Submitted', // 2,
  Declined = 'Declined', // 3,
  Closed = 'Closed', // 4,
  Completed = 'Completed', // 5,
  PendingApproval = 'PendingApproval', // 6,
  /** Only used in the UI for catching unknown status */
  NotStarted = 'NotStarted',
}

export enum CustomReportDisplayMode {
  MyReports = '1',
  IncludeSharedReports = '3',
}

export enum PGInvitationStatus {
  Accepted = 'Accepted',
  Declined = 'Declined',
  Initiated = 'Initiated',
  Submitted = 'Submitted',
  Withdrawn = 'Withdrawn',
}

export enum StripeProductNames {
  UNIT_PAYMENTS = 'ScoutBookPlus',
}

export enum StripePaymentTypes {
  CreditCard = 'credit_card',
  PaymentBank = 'payment_bank',
  AllTypes = '',
}

export enum StripePaymentAccountStates {
  Prestaging = 'prestaging',
  Staged = 'staged',
  Initiated = 'Initiated',
  LinkGenerated = 'link_generated',
  Onboarded = 'Onboarded',
  onboarded = 'onboarded', // Can be either uppercase or not (?)
  Hold = 'hold',
}

export enum CounselorAvailabilities {
  Busy = 'CA_Busy',
  Available = 'CA_Available',
}

export enum ActivityLogType {
  SERVICE_PROJECTS = 1,
  CAMPOUTS = 2,
  HIKES = 3,
  LONG_CRUISE = 4,
  EAGLE_SERVICE_PROJECT = 5,
}

export enum ReportGroupings {
  Membership = 'Membership',
  Quick_Export = 'Quick Export',
  Activity = 'Activity',
  Unit = 'Unit',
  Advancement = 'Advancement',
  Finance = 'Finance',
  Calendar = 'Calendar',
  Not_Grouped = '',
}
